/**
 * Wire format for a level lobby. The editor server stores the member list and the last
 * host-accepted document. The host's browser is the only physics simulation.
 */

export const MAX_LOBBY_MEMBERS = 16;
export const MAX_PLAYERS = 4;

export interface LobbySummary {
  id: string;
  levelId: string;
  levelName: string;
  hostId: string;
  hostEmail: string | null;
  members: number;
  players: number;
  mode: 'edit' | 'play';
}

export interface MemberView {
  id: string;
  email: string | null;
  slot: number | null;
  /** every player slot this person controls, including slot */
  slots: number[];
  host: boolean;
  /** picked cursor colour '#rrggbb' (null: one derived from the id) */
  color: string | null;
  /** picked display name (null: the email before the @) */
  name: string | null;
}

export const MAX_NAME = 24;

/** '#rrggbb' in lower case, else null. */
export function cleanColor(v: unknown): string | null {
  return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : null;
}

/** A display name without control characters or runs of spaces, at most MAX_NAME long; null when empty. */
export function cleanName(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.replace(/[\p{Cc}\p{Cf}]/gu, '').replace(/\s+/g, ' ').trim().slice(0, MAX_NAME).trim();
  return s || null;
}

export interface LockView {
  id: string;
  accountId: string;
}

export interface RoomView {
  id: string;
  levelId: string;
  levelName: string;
  hostId: string;
  mode: 'edit' | 'play';
  rev: number;
  self: string;
  members: MemberView[];
  locks: LockView[];
}

export interface NetVec {
  x: number;
  y: number;
}

/** Button edges are true for a single host tick. restart is ignored from guests. */
export interface NetInput {
  movement: NetVec;
  control: NetVec;
  jumpPressed: boolean;
  jumpReleased: boolean;
  dashPressed: boolean;
  interactPressed: boolean;
  interactDown: boolean;
  powerupPressed: boolean;
  powerupReleased: boolean;
  restartPressed: boolean;
  claimCameraPressed: boolean;
  /** Ctrl / left shoulder: fly with the control stick, and d-pad up/down changes lightness */
  controlEnabled: boolean;
  /** 0..1, Z / left trigger. With d-pad up, joins another player on this device. */
  leftTrigger: number;
  /** 0..1, X / right trigger. Speeds up debug flight. */
  rightTrigger: number;
  dUp: boolean;
  dDown: boolean;
  dLeft: boolean;
  dRight: boolean;
  /** Gamepad Start / P. One tick. Toggles pause on the host. */
  pausePressed: boolean;
}

export interface NetEdges {
  jumpPressed: boolean;
  jumpReleased: boolean;
  dashPressed: boolean;
  interactPressed: boolean;
  powerupPressed: boolean;
  powerupReleased: boolean;
  claimCameraPressed: boolean;
  dUp: boolean;
  dDown: boolean;
  dLeft: boolean;
  dRight: boolean;
  pausePressed: boolean;
}

export function emptyEdges(): NetEdges {
  return {
    jumpPressed: false,
    jumpReleased: false,
    dashPressed: false,
    interactPressed: false,
    powerupPressed: false,
    powerupReleased: false,
    claimCameraPressed: false,
    dUp: false,
    dDown: false,
    dLeft: false,
    dRight: false,
    pausePressed: false,
  };
}

/** Press and release are separate ticks so a tap is not cancelled by its own release. */
export function splitEdges(input: NetInput): NetEdges[] {
  const press = emptyEdges();
  press.jumpPressed = input.jumpPressed;
  press.dashPressed = input.dashPressed;
  press.interactPressed = input.interactPressed;
  press.powerupPressed = input.powerupPressed;
  press.claimCameraPressed = input.claimCameraPressed;
  press.dUp = input.dUp;
  press.dDown = input.dDown;
  press.dLeft = input.dLeft;
  press.dRight = input.dRight;
  press.pausePressed = input.pausePressed;
  const release = emptyEdges();
  release.jumpReleased = input.jumpReleased;
  release.powerupReleased = input.powerupReleased;
  const out: NetEdges[] = [];
  if (Object.values(press).some(Boolean)) out.push(press);
  if (Object.values(release).some(Boolean)) out.push(release);
  return out;
}

export interface NetBody {
  id: string;
  x: number;
  y: number;
  a: number;
  vx: number;
  vy: number;
  hide: boolean;
  /** sprite colour, sent for blocks whose tint changes (colour match) */
  tint?: [number, number, number, number];
  /** colour only; the guest must not move this body */
  tintOnly?: boolean;
}

export interface NetPlayer {
  slot: number;
  x: number;
  y: number;
  a: number;
  state: number;
  display: number;
  coins: number;
  deaths: number;
  hide: boolean;
  /** true while the host is in a dash, so guests can draw the trail */
  dash: boolean;
  color: [number, number, number];
  /** debug flight: draw on the "This is your brain on drugs" sorting layer */
  fly?: boolean;
  /** king of the hill life label, when that mode is running */
  lives?: string;
  /** PlayerController.soundLoops: jetpack, wall slide and push loops */
  loops?: number;
}

export interface NetEvent {
  kind: string;
  name?: string;
  what?: string;
  player?: number;
  x?: number;
  y?: number;
}

/** Whether a player-to-player string is still on. Positions are redrawn locally. */
export interface NetLink {
  a: number;
  b: number;
  on: boolean;
}

export interface NetKoth {
  slot: number;
  lives: number;
  text: string;
  color: [number, number, number];
}

export interface NetVolley {
  left: number;
  right: number;
  color?: [number, number, number, number];
}

export interface NetSnap {
  cam: { x: number; y: number; size: number };
  players: NetPlayer[];
  bodies: NetBody[];
  events: NetEvent[];
  links?: NetLink[];
  /** null while play is running */
  paused?: { debug: boolean; slot: number } | null;
  koth?: NetKoth[];
  volley?: NetVolley;
}

export type ClientMsg =
  | { t: 'list' }
  | { t: 'create'; levelId: string }
  | { t: 'join'; lobbyId: string }
  | { t: 'leave' }
  | { t: 'cursor'; x: number; y: number; sel: string[] }
  | { t: 'profile'; color: string | null; name: string | null }
  | { t: 'lock'; ids: string[] }
  | { t: 'unlock'; ids: string[] }
  | { t: 'propose'; baseRev: number; doc: unknown }
  | { t: 'commit'; baseRev: number; doc: unknown; from?: string }
  | { t: 'reject'; accountId: string }
  | { t: 'slot'; slot: number | null }
  | { t: 'assign'; accountId: string; slot: number | null }
  | { t: 'kick'; accountId: string }
  | { t: 'transfer'; accountId: string }
  | { t: 'play'; spawn?: [number, number] }
  | { t: 'stop' }
  | { t: 'take' }
  | { t: 'input'; input: NetInput; slot?: number }
  | { t: 'snap'; snap: NetSnap }
  | { t: 'menu'; cmd: string; n?: number }
  | { t: 'asset'; kind: string; id: string; data: unknown }
  | { t: 'ping' };

export type ServerMsg =
  | { t: 'lobbies'; lobbies: LobbySummary[] }
  | { t: 'joined'; room: RoomView; doc: unknown }
  | { t: 'room'; room: RoomView }
  | { t: 'left' }
  | { t: 'kicked' }
  | { t: 'replaced' }
  | { t: 'error'; error: string }
  | { t: 'cursor'; accountId: string; x: number; y: number; sel: string[] }
  | { t: 'propose'; from: string; baseRev: number; doc: unknown }
  | { t: 'doc'; rev: number; doc: unknown; from: string }
  | { t: 'reject'; rev: number; doc: unknown }
  | { t: 'play'; rev: number; doc: unknown; slots: (string | null)[]; spawn?: [number, number] }
  | { t: 'stop'; rev: number; doc: unknown; reason?: string }
  | { t: 'slots'; slots: (string | null)[] }
  | { t: 'input'; accountId: string; slot: number; input: NetInput }
  | { t: 'snap'; snap: NetSnap }
  | { t: 'menu'; cmd: string; n?: number }
  | { t: 'asset'; from: string; kind: string; id: string; data: unknown }
  | { t: 'pong' };

export function isLevelWire(v: unknown): v is { format: 'platformer-level'; id: string; name?: string } {
  if (!v || typeof v !== 'object') return false;
  const doc = v as { format?: unknown; id?: unknown };
  return doc.format === 'platformer-level' && typeof doc.id === 'string' && /^[\w.-]{1,128}$/.test(doc.id);
}
